import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { InventoryCar, PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { CarService } from 'src/app/services/car/car.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';

@Component({
  selector: 'app-car-listing',
  templateUrl: './car-listing.component.html',
  styleUrls: ['./car-listing.component.scss']
})
export class CarListingComponent extends DestructorComponent implements OnInit {

  // Data properties
  cars: InventoryCar[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Loading states
  isLoading: boolean = false;
  isSearching: boolean = false;

  // Search and filter properties
  searchTerm: string = '';
  selectedMake: string = '';
  selectedModel: string = '';
  currentPage: number = 1;

  // Multiple selection properties
  selectedCars: InventoryCar[] = [];
  selectAll: boolean = false;

  // Output event to communicate with parent component
  @Output() carsSelected = new EventEmitter<InventoryCar[]>();

  // Dropdown data
  makesList: any[] = [];
  modelsList: any[] = [];

  // Search debounce
  private searchSubject = new Subject<string>();

  // Expose Math for template
  Math = Math;

  constructor(
    private _carService: VehicleService,
    private _generalService: GeneralService
  ) {
    super();
    this.setupSearchDebounce();
  }

  ngOnInit(): void {
    this.loadMakes();
    this.setupModalEventListeners();
  }

  private setupModalEventListeners(): void {
    const modalElement = document.getElementById('kt_modal_car_listing');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        // Clear previous selections and load cars when modal is opened
        this.clearSelections();
        this.loadCars();
      });
    }
  }

  private setupSearchDebounce(): void {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchTerm = searchTerm;
      this.currentPage = 1;
      this.loadCars();
    });
  }

  loadMakes(): void {
    this.subscriptions.push(
      this._generalService.GetMakes().subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.makesList = response.data.map((make: any) => ({
              value: make.slug || make._id,
              label: make.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading makes:', error);
        }
      })
    );
  }

  loadModels(makeSlug: string): void {
    if (!makeSlug) {
      this.modelsList = [];
      return;
    }

    this.subscriptions.push(
      this._generalService.GetModels(makeSlug).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.modelsList = response.data.map((model: any) => ({
              value: model.slug || model._id,
              label: model.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading models:', error);
          this.modelsList = [];
        }
      })
    );
  }

  loadCars(): void {
    this.isLoading = true;

    // Prepare filters
    const filters: Record<string, any> = {
      is_sold:false
    };
    if (this.selectedMake) {
      filters['make'] = this.selectedMake;
    }
    if (this.selectedModel) {
      filters['model'] = this.selectedModel;
    }

    this.subscriptions.push(
      this._carService.GetData(this.currentPage.toString(), this.searchTerm, filters).subscribe({
        next: (response: PaginatedResponse<InventoryCar>) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.cars = response.data.data || [];
            this.paginationInfo = response.data.pagination;
          } else {
            this.cars = [];
            this.resetPagination();
          }
          // Update select all state after loading new data
          this.updateSelectAllState();
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading cars:', error);
          this.cars = [];
          this.resetPagination();
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchSubject.next(target.value);
  }

  onMakeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedMake = target.value;
    this.selectedModel = ''; // Reset model when make changes
    this.modelsList = []; // Clear models list

    if (this.selectedMake) {
      this.loadModels(this.selectedMake);
    }
  }

  onModelChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedModel = target.value;
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadCars();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadCars();
  }

  // Multiple selection methods
  toggleCarSelection(car: InventoryCar, event: Event): void {
    const checkbox = event.target as HTMLInputElement;

    if (checkbox.checked) {
      // Add car to selection if not already selected
      if (!this.isCarSelected(car)) {
        this.selectedCars.push(car);
      }
    } else {
      // Remove car from selection
      this.selectedCars = this.selectedCars.filter(selectedCar => selectedCar._id !== car._id);
    }

    // Update select all checkbox state
    this.updateSelectAllState();
  }

  toggleSelectAll(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.selectAll = checkbox.checked;

    if (this.selectAll) {
      // Select all visible cars that are not sold
      this.cars.forEach(car => {
        if (!car.is_sold && !this.isCarSelected(car)) {
          this.selectedCars.push(car);
        }
      });
    } else {
      // Deselect all visible cars
      this.cars.forEach(car => {
        this.selectedCars = this.selectedCars.filter(selectedCar => selectedCar._id !== car._id);
      });
    }
  }

  isCarSelected(car: InventoryCar): boolean {
    return this.selectedCars.some(selectedCar => selectedCar._id === car._id);
  }

  private updateSelectAllState(): void {
    const availableCars = this.cars.filter(car => !car.is_sold);
    const selectedAvailableCars = availableCars.filter(car => this.isCarSelected(car));

    this.selectAll = availableCars.length > 0 && selectedAvailableCars.length === availableCars.length;
  }

  addSelectedCars(): void {
    if (this.selectedCars.length === 0) {
      console.warn('No cars selected');
      return;
    }

    console.log('Selected cars to add:', this.selectedCars);
    console.log('Number of cars selected:', this.selectedCars.length);

    // Log detailed information about selected cars
    this.selectedCars.forEach((car, index) => {
      console.log(`Car ${index + 1}:`, {
        id: car._id,
        name: car.name,
        inventory_no: car.inventory_no,
        company_name: car.company_name,
        year: car.year,
        vin_no: car.vin_no,
        price: car.price,
        mileage: car.mileage,
        fuel_type: car.fuel_type,
        color: car.color
      });
    });

    // Emit selected cars to parent component
    this.carsSelected.emit([...this.selectedCars]); // Create a copy to avoid reference issues

    // Clear selections and close modal
    this.clearSelections();
    this.closeModal();
  }

  clearSelections(): void {
    this.selectedCars = [];
    this.selectAll = false;
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_car_listing');
    if (modalElement) {
      const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Keep the single select method for backward compatibility
  selectCar(car: InventoryCar): void {
    // Add to selection instead of immediate action
    if (!this.isCarSelected(car)) {
      this.selectedCars.push(car);
      this.updateSelectAllState();
    }
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'JPY',
      minimumFractionDigits: 0
    }).format(price);
  }

  formatMileage(mileage: number): string {
    return new Intl.NumberFormat('en-US').format(mileage) + ' km';
  }

  getMainImage(car: InventoryCar): string {
    if (car.pictures && car.pictures.length > 0) {
      return car.type=="aucnet"? car.pictures[0] : (car.pictures.length > 1?car.pictures[1]:car.pictures[0]);
    }
    return 'assets/media/stock/600x400/img-1.jpg'; // Fallback image
  }

}
